import { motion, useReducedMotion } from 'framer-motion';
import { transition } from '../lib/motion.js';

const EFFECT_ICON = {
  LIGHT: '💡',
  MUSIC_SHIFT: '🎵',
  SMOKE: '💨',
  FREE_SHOT: '🥃',
  DJ_MODE: '🎧',
  CROWD_INTERACTION: '🙌',
};

const EFFECT_UNLOCK_LABEL_FR = {
  LIGHT: 'Jeu de lumières',
  MUSIC_SHIFT: 'Changement musical',
  SMOKE: 'Machine à fumée',
  FREE_SHOT: 'Tournée de shots',
  DJ_MODE: 'Mode peak DJ',
  CROWD_INTERACTION: 'Interaction foule',
};

function formatAmount(cents) {
  const value = Math.max(0, Number(cents) || 0);
  return `${(value / 100).toFixed(2)} €`;
}

function formatEurosTight(cents) {
  const value = Math.max(0, Number(cents) || 0);
  return `${(value / 100).toLocaleString('fr-FR', {
    minimumFractionDigits: value % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  })} €`;
}

function nextMilestoneMeta(steps, currentAmountCents) {
  const ordered = [...steps].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  if (!ordered.length) {
    return {
      capCents: 1,
      headline: null,
      emoji: '⚡',
      nextOpen: null,
      allComplete: false,
    };
  }

  const nextOpen = ordered.find((s) => !s.is_triggered);
  const capCents = nextOpen
    ? nextOpen.threshold_cents
    : ordered[ordered.length - 1].threshold_cents;

  if (!nextOpen) {
    return {
      capCents,
      headline: 'Tous les paliers de la soirée sont débloqués.',
      emoji: '🎉',
      nextOpen: null,
      allComplete: true,
    };
  }

  const label =
    EFFECT_UNLOCK_LABEL_FR[nextOpen.effect_type] ?? nextOpen.title ?? 'Effet spécial';
  const emoji = EFFECT_ICON[nextOpen.effect_type] ?? '⚡';
  const threshold = nextOpen.threshold_cents ?? 0;
  const reachedMoney = currentAmountCents >= threshold;
  const remaining = Math.max(0, threshold - currentAmountCents);

  const headline = reachedMoney
    ? `Objectif financier atteint — prochain déclenchement côté DJ : ${label}`
    : remaining > 0
      ? `Plus que ${formatEurosTight(remaining)} pour débloquer : ${label}`
      : `Prochain palier : ${label}`;

  return { capCents, headline, emoji, nextOpen, allComplete: false };
}

function getStepState(step, currentAmountCents) {
  const reachedThreshold = currentAmountCents >= (step.threshold_cents ?? 0);
  if (reachedThreshold && step.is_triggered) return 'reached';
  if (reachedThreshold && !step.is_triggered) return 'pending';
  return 'locked';
}

function getStepTone(stepState) {
  if (stepState === 'reached') {
    return {
      badge: 'bg-emerald-500/20 text-emerald-200 border-emerald-400/35',
      marker: 'bg-emerald-400 border-emerald-200',
      label: 'Débloqué',
      row: 'border-emerald-500/15 bg-emerald-500/[0.07]',
    };
  }
  if (stepState === 'pending') {
    return {
      badge: 'bg-amber-500/25 text-amber-200 border-amber-400/40',
      marker: 'bg-amber-400 border-amber-100 animate-pulse',
      label: 'En attente DJ',
      row: 'border-amber-500/20 bg-amber-500/[0.08]',
    };
  }
  return {
    badge: 'bg-white/5 text-white/55 border-white/12',
    marker: 'bg-white/25 border-white/35',
    label: 'À venir',
    row: 'border-white/10 bg-black/25',
  };
}

export default function DonationGoalWidget({ goal, pulseToken, topContributors = [] }) {
  const reduceMotion = useReducedMotion();

  if (!goal) {
    return null;
  }

  const noGoalRow = Boolean(goal.no_goal_row);
  const isInactive = goal.is_active === false;
  const hasSteps = Array.isArray(goal.steps) && goal.steps.length > 0;
  const maxThreshold = Math.max(goal.max_threshold_cents ?? 0, 1);
  const currentAmount = Math.max(0, goal.current_amount_cents ?? 0);
  const towardGlobal = Math.max(0, Math.min(100, (currentAmount / maxThreshold) * 100));

  const showProgress = hasSteps && goal.is_active !== false;
  const milestone =
    hasSteps && !noGoalRow && goal.is_active !== false
      ? nextMilestoneMeta(goal.steps, currentAmount)
      : null;

  const nextThreshold = milestone?.nextOpen?.threshold_cents ?? milestone?.capCents ?? maxThreshold;
  const progressToNext =
    milestone?.nextOpen && nextThreshold > 0
      ? Math.max(0, Math.min(100, (currentAmount / nextThreshold) * 100))
      : towardGlobal;

  const displayPercent =
    milestone?.allComplete === true ? 100 : Number(progressToNext.toFixed(1));

  return (
    <motion.section
      className="card mt-3"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={transition(reduceMotion, 'panel')}
    >
      <div>
        {showProgress && milestone?.headline ? (
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-white/45">
              Cagnotte de la soirée
            </p>
            <p className="mt-2 font-mono text-2xl font-bold tabular-nums text-gold">
              {formatEurosTight(currentAmount)}
            </p>
            <p className="mt-1 text-sm leading-relaxed text-white/55">
              collectés · max{' '}
              <span className="font-medium text-white/75">{formatEurosTight(maxThreshold)}</span>
              {milestone?.allComplete ? null : (
                <>
                  {' '}
                  ·{' '}
                  <span className="font-medium text-gold">
                    <span className="font-mono tabular-nums">{displayPercent}%</span> vers le prochain palier
                  </span>
                </>
              )}
            </p>

              <div className="relative mt-4">
                <div className="h-2 overflow-hidden rounded-full bg-white/5">
                  <motion.div
                    className="h-full rounded-full bg-accent"
                    initial={{ width: 0 }}
                    animate={{
                      width: `${milestone?.allComplete ? 100 : progressToNext}%`,
                    }}
                    transition={transition(reduceMotion, 'medium')}
                  />
                </div>

                {hasSteps ? (
                  <div className="relative mt-2 h-4">
                    {goal.steps.map((step) => {
                      const threshold = Math.max(0, step.threshold_cents ?? 0);
                      const left = Math.max(0, Math.min(100, (threshold / maxThreshold) * 100));
                      const stepState = getStepState(step, currentAmount);
                      const tone = getStepTone(stepState);
                      return (
                        <span
                          key={step.id}
                          className={`absolute top-0 h-3 w-3 -translate-x-1/2 rounded-full border-2 shadow-md ${tone.marker}`}
                          style={{ left: `${left}%` }}
                          title={`${formatAmount(threshold)} — ${step.title}`}
                        />
                      );
                    })}
                  </div>
                ) : null}
              </div>

              <div className="mt-4 rounded-xl border border-border bg-bg-soft px-3 py-3">
                <p className="flex flex-wrap items-center gap-2 text-sm font-medium leading-snug text-white/90">
                  <span className="text-lg leading-none" aria-hidden>
                    {milestone.emoji}
                  </span>
                  {milestone.headline}
                </p>
                <p className="mt-2 text-[11px] leading-relaxed text-white/45">
                  Montant = paiements enregistrés sur la soirée (aligné sur le pot de l’en-tête). Les effets
                  se déclenchent côté DJ.
                </p>
              </div>
          </div>
        ) : (
          <div className="rounded-xl border border-border bg-bg-soft px-4 py-4">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-white/45">
              Cagnotte
            </p>
            <p className="mt-2 text-sm text-white/70">
              {noGoalRow
                ? "Pas encore de cagnotte pour cette soirée."
                : isInactive && hasSteps
                  ? 'La cagnotte est momentanément inactive.'
                  : 'Ici s’affichera la progression collective quand la soirée aura des paliers.'}
            </p>
          </div>
        )}

        {!hasSteps ? (
          // Détail de configuration réservé au DEV : l'invité voit déjà le message
          // neutre ci-dessus (« Pas encore de cagnotte »), pas de jargon admin.
          goal.has_error ? (
            <div className="mt-4 rounded-2xl border border-dashed border-white/15 bg-black/20 p-3 text-[12px] leading-relaxed text-white/50">
              Impossible de charger les paliers pour le moment.
            </div>
          ) : import.meta.env.DEV ? (
            <div className="mt-4 rounded-2xl border border-dashed border-white/15 bg-black/20 p-3 text-[12px] leading-relaxed text-white/50">
              {goal.no_goal_row
                ? 'Configuration : lie un template de paliers à cet événement (donation_goal_events).'
                : goal.is_active === false && !goal.no_goal_row
                  ? 'Réactive la cagnotte côté admin pour afficher les montants.'
                  : 'Aucun palier n’est défini sur ce template.'}
            </div>
          ) : null
        ) : (
          <div className="mt-6">
            <p className="mb-3 text-[11px] font-semibold uppercase tracking-widest text-white/40">
              Paliers
            </p>
            <ul className="space-y-2.5">
              {goal.steps.map((step) => {
                const stepState = getStepState(step, currentAmount);
                const tone = getStepTone(stepState);
                const icon = EFFECT_ICON[step.effect_type] ?? '✨';
                const shouldPulse = pulseToken > 0 && step.is_triggered;
                const threshold = step.threshold_cents ?? 0;
                const slice = maxThreshold > 0 ? Math.min(100, (threshold / maxThreshold) * 100) : 0;

                return (
                  <motion.li
                    key={step.id}
                    className={`rounded-2xl border p-3.5 ${tone.row}`}
                    animate={shouldPulse ? { scale: [1, 1.008, 1] } : undefined}
                    transition={{ duration: 0.45 }}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-black/30 text-xl">
                        {icon}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="truncate text-[15px] font-semibold text-white">{step.title}</p>
                          <span
                            className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${tone.badge}`}
                          >
                            {tone.label}
                          </span>
                        </div>
                        {step.description ? (
                          <p className="mt-1 text-xs leading-relaxed text-white/55">{step.description}</p>
                        ) : null}
                        <div className="mt-2.5 flex items-center justify-between gap-2 text-[11px] text-white/45">
                          <span>Seuil {formatAmount(threshold)}</span>
                          <span className="tabular-nums text-white/35">{slice.toFixed(0)} % de la jauge max</span>
                        </div>
                      </div>
                    </div>
                  </motion.li>
                );
              })}
            </ul>
          </div>
        )}

        {topContributors.length > 0 ? (
          <div className="mt-6 rounded-xl border border-accent/25 bg-accent/10 px-4 py-3.5">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-accent">
              Top contributeurs ce soir
            </p>
            <ul className="mt-3 divide-y divide-white/5">
              {topContributors.slice(0, 5).map((c, idx) => {
                const medal = idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : '✨';
                return (
                  <li
                    key={c.bar_user_id}
                    className="flex items-center justify-between gap-3 py-2.5 first:pt-0 last:pb-0"
                  >
                    <span className="min-w-0 truncate text-[14px] font-medium text-white/92">
                      <span className="mr-1.5 opacity-90">{medal}</span>
                      {c.username}
                    </span>
                    <span className="shrink-0 font-mono text-[15px] font-bold tabular-nums text-gold">
                      {formatEurosTight(c.totalCents)}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}
      </div>
    </motion.section>
  );
}
